import { Button, Select, TextInput, Textarea, Tooltip } from 'flowbite-react';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import Loader from '../Common/Loader';
import { Link, useNavigate, useParams } from 'react-router-dom';
import UserApi from '../../Api/UserApi';
import { User } from '../../Models/User';
import { fetchClients, fetchExistingEmails } from '../../Actions/CommonFields';
import { useAppDispatch, useAppSelector } from '../../Reducers/store';
import { HiEye, HiInformationCircle } from 'react-icons/hi';
import UserUpdatePassword from './UserUpdatePassword';
import { Client } from '../../Models/Client';
import AuthApi from '../../Api/AuthApi';
import { RecoverPasswordRequest } from '../../Models/RecoverPasswordRequest';

export default function RegisterUser() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { existingEmails, clients } = useAppSelector(
    (state) => state.commonFields
  );
  const [userData, setUserData] = useState<User | null>();
  const [clientData, setClientData] = useState<Client | null>();
  const [clientError, setClientError] = useState<boolean>(false);
  const [clientNumber, setClientNumber] = useState<string>('');
  const [show, setShow] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(false);
  const [emailExists, setEmailExists] = useState<boolean>(false);
  const [showEmailSent, setShowEmailSent] = useState(false);
  const [formData, setFormData] = useState<User>({
    id: 0,
    firstName: '',
    lastName: '',
    emailAddress: '',
    phoneNumber: '',
    birthdate: null,
    userLevelId: 3,
    isPrimary: false,
    address: '',
    city: '',
    state: '',
    zip: '',
    bio: '',
    clientId: null,
    emailConfirmed: null,

    client: null,
    userLevel: null,
  });

  const getReferences = async () => {
    if (!existingEmails.length || !clients?.length) {
      await Promise.all([
        dispatch(fetchExistingEmails()),
        dispatch(fetchClients()),
      ]);
    }
  };

  useEffect(() => {
    getReferences();
  }, []);

  const checkEmail = (email: string) => {
    if (existingEmails.some((x) => x === email.trim().toLowerCase())) {
      setButtonDisabled(true);
      setEmailExists(true);
    } else {
      setButtonDisabled(false);
      setEmailExists(false);
    }
  };

  const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
    if (name === 'emailAddress') {
      checkEmail(value);
    }
  };

  const handleChangeAccountNumber = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setClientNumber(e.target.value);
  };

  const onSubmit = async (e: React.ChangeEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (clientData) {
      setButtonDisabled(true);
      try {
        setIsLoading(true);
        formData.clientId = clientData.id;
        return AuthApi.createUser(formData)
          .then((res) => {
            if (res.data.success === true) {
              setShowEmailSent(true);
            } else {
              toast.error(res.data.message);
              setButtonDisabled(false);
            }
            setIsLoading(false);
          })
          .catch((err) => {
            toast.error('Error: ' + err);
            setIsLoading(false);
            setButtonDisabled(false);
          });
      } catch (error) {
        console.error(error);
        setIsLoading(false);
        setButtonDisabled(false);
      }
    }
  };

  const handleAccountNumberClick = (accountNumber: string) => {
    const client = clients.find(
      (x) =>
        x.accountNumber !== null &&
        x.accountNumber.trim().toLowerCase() ===
          accountNumber.trim().toLowerCase()
    );
    if (client) {
      setClientData(client);
    } else {
      setClientError(true);
    }
  };

  return (
    <div className='w-full'>
      <div className='w-full p-4'>
        <div className='mx-1 flex items-end'>
          <div className='flex flex-1'>
            <h1 className='text-gray-900 font-bold text-4xl'>Register</h1>
          </div>
        </div>
      </div>

      <div>
        <div className='container mx-auto p-2'>
          <div className='md:flex no-wrap md:-mx-2 '>
            <div className='w-full mx-2'>
              <div className='bg-white p-3'>
                <hr className='bg-gray-300 h-[5px] mb-2'></hr>
                {!clientData && (
                  <div className='text-gray-500'>
                    <div className='flex justify-center gap-10 text-xl'>
                      <div className='flex'>
                        <div className='font-bold content-center'>
                          Enter Account Number:
                        </div>
                      </div>
                      <div className='w-1/4'>
                        <TextInput
                          id='clientNumber'
                          name='clientNumber'
                          type='text'
                          placeholder='Account #'
                          onChange={handleChangeAccountNumber}
                          value={clientNumber}
                          sizing='md'
                        />
                      </div>
                    </div>
                    {clientError && (
                      <div className='flex justify-center gap-10 text-xl text-red-500 font-bold'>
                        Unable to find account. Try again or contact Fuel Pro
                        USA to set up Account
                      </div>
                    )}
                    <div className='flex justify-center mt-2'>
                      <Button
                        disabled={buttonDisabled}
                        type='button'
                        className='buttonMain'
                        onClick={() => handleAccountNumberClick(clientNumber)}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
                {clientData && (
                  <form id='registerUser' onSubmit={onSubmit}>
                    <div className='text-gray-500'>
                      <div className='grid grid-cols-[40%_60%] md:grid-cols-[25%_75%] xl:grid-cols-[14%_86%] text-xl'>
                        <div className='flex'>
                          <div className='font-bold content-center'>
                            Account:
                          </div>
                        </div>
                        <div className='w-full text-black'>
                          {clientData.name} in {clientData.city}
                          {clientData.stateId ? `, ${clientData.stateId}` : ''}
                        </div>
                      </div>
                      <hr className='bg-gray-300 h-[2px]'></hr>
                      <div className='grid grid-cols-[40%_60%] md:grid-cols-[25%_75%] xl:grid-cols-[14%_86%] text-xl'>
                        <div className='flex'>
                          <div className='font-bold content-center'>Name:</div>
                        </div>
                        <div className='flex'>
                          <div className='w-1/2 md:w-1/3'>
                            <TextInput
                              id='firstName'
                              name='firstName'
                              type='text'
                              placeholder='First Name...'
                              onChange={handleChangeInput}
                              value={formData.firstName ?? ''}
                              sizing='md'
                            />
                          </div>
                          <div className='w-1/2 md:w-1/3'>
                            <TextInput
                              id='lastName'
                              name='lastName'
                              type='text'
                              placeholder='Last Name...'
                              onChange={handleChangeInput}
                              value={formData.lastName ?? ''}
                              sizing='md'
                            />
                          </div>
                        </div>
                      </div>
                      <hr className='bg-gray-300 h-[2px]'></hr>
                      <div className='grid grid-cols-[40%_60%] md:grid-cols-[25%_75%] xl:grid-cols-[14%_86%] text-xl'>
                        <div className='flex'>
                          <div className='font-bold content-center'>Email:</div>
                        </div>
                        <div className='flex'>
                          <div className='w-1/2 md:w-1/3'>
                            <TextInput
                              id='emailAddress'
                              name='emailAddress'
                              type='text'
                              placeholder='Email Address...'
                              onChange={handleChangeInput}
                              value={formData.emailAddress ?? ''}
                              sizing='md'
                              required
                            />
                          </div>
                          {emailExists && (
                            <div className='flex w-1/2 md:w-2/3 text-red-600'>
                              <div className='content-center px-2'>
                                <HiInformationCircle />
                              </div>
                              <div className='content-center text-sm md:text-lg'>
                                Email already exists
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <hr className='bg-gray-300 h-[2px]'></hr>
                      <div className='grid grid-cols-[40%_60%] md:grid-cols-[25%_75%] xl:grid-cols-[14%_86%] text-xl'>
                        <div className='flex'>
                          <div className='font-bold content-center'>Phone:</div>
                        </div>
                        <div className='w-1/2 md:w-1/3'>
                          <TextInput
                            id='phoneNumber'
                            name='phoneNumber'
                            type='text'
                            placeholder='Phone...'
                            onChange={handleChangeInput}
                            value={formData.phoneNumber ?? ''}
                            sizing='md'
                          />
                        </div>
                      </div>
                    </div>
                    {showEmailSent && (
                      <div className='flex justify-center mt-5'>
                        A confirmation email has been sent
                      </div>
                    )}
                    {!showEmailSent && (
                      <div className={'flex justify-end pt-2'}>
                        <Button
                          disabled={buttonDisabled}
                          type='submit'
                          className='buttonMain'
                          form='registerUser'
                        >
                          Save
                        </Button>
                      </div>
                    )}
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
